// src/pages/marketplace/admin/AdminProductsPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import type { ProductStatus as ApiProductStatus } from '@core/types/product';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { Select } from '@/components/Select';

type ProductStatus = 'DRAFT' | 'BETA' | 'ACTIVE' | 'MAINTENANCE' | 'DEPRECATED' | 'DISABLED';

const PRODUCT_STATUSES: ProductStatus[] = [
  'DRAFT',
  'BETA',
  'ACTIVE',
  'MAINTENANCE',
  'DEPRECATED',
  'DISABLED',
];

const PRODUCT_TYPES = ['all', 'builtin', 'third-party'] as const;

/** Convert short status names ('DRAFT') to full API enum values ('PRODUCT_STATUS_DRAFT'). */
function toApiProductStatus(status: string): ApiProductStatus {
  return (status.startsWith('PRODUCT_STATUS_')
    ? status
    : `PRODUCT_STATUS_${status}`) as ApiProductStatus;
}

function mapProductStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'BETA':
      return 'info';
    case 'DRAFT':
      return 'secondary';
    case 'MAINTENANCE':
      return 'warning';
    case 'DEPRECATED':
      return 'warning';
    case 'DISABLED':
      return 'error';
    default:
      return 'secondary';
  }
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(dateStr);
  }
}

/**
 * Admin view of all products in the marketplace.
 * Route: /admin/products
 */
export const AdminProductsPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusAction, setStatusAction] = useState<{ id: string; show: boolean } | null>(null);
  const [statusValue, setStatusValue] = useState<ProductStatus>('ACTIVE');
  const [statusReason, setStatusReason] = useState('');

  const loadProducts = useCallback(() => {
    const params: Record<string, any> = {
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
    };
    if (statusFilter !== 'all') params.status = toApiProductStatus(statusFilter);
    if (typeFilter === 'builtin') params.is_builtin = true;
    if (typeFilter === 'third-party') params.is_builtin = false;
    if (search) params.search = search;

    store.ListProducts(params);
  }, [searchParams, statusFilter, typeFilter, search]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (typeFilter !== 'all') params.type = typeFilter;
    setSearchParams(params);
  };

  const handleFilterChange = (key: string, value: string) => {
    const params: Record<string, string> = {};
    const newSearch = key === 'search' ? value : search;
    const newStatus = key === 'status' ? value : statusFilter;
    const newType = key === 'type' ? value : typeFilter;

    if (newSearch) params.search = newSearch;
    if (newStatus !== 'all') params.status = newStatus;
    if (newType !== 'all') params.type = newType;

    if (key === 'status') setStatusFilter(value);
    if (key === 'type') setTypeFilter(value);

    setSearchParams(params);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === store.items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(store.items.map((item: any) => item.entity?.id || item.id)));
    }
  };

  const handleBulkAction = async (status: 'ACTIVE' | 'DISABLED') => {
    const ids = Array.from(selectedIds);
    for (const id of ids) {
      await store.SetProductStatus(id, {
        status: toApiProductStatus(status),
        reason: `Bulk ${status.toLowerCase()} action`,
      });
    }
    setSelectedIds(new Set());
    loadProducts();
  };

  const handleSetStatus = async (id: string) => {
    await store.SetProductStatus(id, {
      status: toApiProductStatus(statusValue),
      reason: statusReason,
    });
    setStatusAction(null);
    setStatusReason('');
    loadProducts();
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) return;
    await store.DeleteProduct(id);
    loadProducts();
  };

  // Compute stats
  const totalProducts = store.items.length;
  const activeProducts = store.items.filter((p: any) => p.status === 'ACTIVE').length;
  const pendingReview = store.items.filter(
    (p: any) => p.status === 'DRAFT' || p.status === 'BETA',
  ).length;

  return (
    <div className="space-y-6">
      {/* Page header with stats. The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">Products Management</h1>
        <div className="flex gap-4">
          <span className="text-sm text-muted-foreground">
            Total: <span className="font-semibold text-foreground">{totalProducts}</span>
          </span>
          <span className="text-sm text-muted-foreground">
            Active: <span className="font-semibold text-green-600">{activeProducts}</span>
          </span>
          <span className="text-sm text-muted-foreground">
            Pending review: <span className="font-semibold text-yellow-600">{pendingReview}</span>
          </span>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/products/categories">
            <Button variant="secondary">Categories</Button>
          </Link>
          <Link to="/admin/products/reviews">
            <Button variant="secondary">Reviews</Button>
          </Link>
          <Link to="/admin/products/verification">
            <Button variant="secondary">Verification</Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select
                value={statusFilter}
                onChange={(e) => handleFilterChange('status', e.target.value)} fullWidth={false}>
                <option value="all">All Statuses</option>
                {PRODUCT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0) + s.slice(1).toLowerCase()}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Type</label>
              <Select value={typeFilter} onChange={(e) => handleFilterChange('type', e.target.value)} fullWidth={false}>
                {PRODUCT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t === 'all' ? 'All Types' : t === 'builtin' ? 'Built-in' : 'Third-party'}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth={false}
                className="w-full"
              />
            </div>

            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {selectedIds.size} product{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleBulkAction('ACTIVE')}
              >
                Enable Selected
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleBulkAction('DISABLED')}
              >
                Disable Selected
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status change dialog */}
      {statusAction?.show && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              Set Product Status
            </h3>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">New Status</label>
                <Select
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value as ProductStatus)} fullWidth={false}>
                  {PRODUCT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0) + s.slice(1).toLowerCase()}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Reason (optional)"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  fullWidth={false}
                  className="w-full"
                />
              </div>
              <Button size="sm" onClick={() => handleSetStatus(statusAction.id)}>
                Apply
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setStatusAction(null);
                  setStatusReason('');
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={store.items.length > 0 && selectedIds.size === store.items.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
              </TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Public</TableHead>
              <TableHead className="text-center">Verified</TableHead>
              <TableHead className="text-right">Installs</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={9} message="No products found" />
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(itemId)}
                        onChange={() => toggleSelect(itemId)}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {item.icon_url ? (
                          <img
                            src={item.icon_url}
                            alt=""
                            className="h-8 w-8 rounded-md object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                            <span className="text-xs font-medium text-muted-foreground">
                              {String(item.name ?? '').charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <Link
                            to={`/admin/products/${itemId}`}
                            className="text-sm font-medium text-foreground hover:text-primary"
                          >
                            {String(item.name ?? '')}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {String(item.slug ?? '')}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {String(item.owner?.name || item.owner || '--')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={mapProductStatusToVariant(item.status)}
                        label={String(item.status ?? 'UNKNOWN')}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      {item.is_public ? (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.is_verified || item.metadata?.is_verified ? (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">--</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm text-foreground tabular-nums">
                        {item.install_count ?? item.metadata?.install_count ?? 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(item.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/admin/products/${itemId}`}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setStatusAction({ id: itemId, show: true })
                          }
                        >
                          Set Status
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleDelete(itemId, item.name)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          Showing {store.items.length} product{store.items.length !== 1 ? 's' : ''}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!searchParams.get('cursor')}
            onClick={() => {
              const params = Object.fromEntries(searchParams.entries());
              delete params.cursor;
              setSearchParams(params);
            }}
          >
            First
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={store.items.length < 20}
            onClick={() => {
              const lastItem = store.items[store.items.length - 1];
              const cursor = lastItem?.entity?.id || '';
              if (cursor) {
                const params = Object.fromEntries(searchParams.entries());
                params.cursor = cursor;
                setSearchParams(params);
              }
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
});

export default AdminProductsPage;
